import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.jsx';
import {
  Shield, Menu, LogOut, ChevronDown, Bell, Search,
  Home, FileText, ScrollText, Map, Radio,
  Target, GitBranch, Search as SearchIcon, AlertCircle,
  Bell as BellIcon, UserCheck, MessagesSquare, Scale,
  Vote, Copy, Workflow,
  LayoutDashboard, ListTree,
  ClipboardList, Building2, ShieldCheck,
  AlertTriangle as BoloIcon,
  Camera as CameraIcon,
  ShieldAlert, HeartPulse, Globe, Gavel, Network, KeyRound,
  Compass, Settings,
  Inbox, Cctv, Truck, Car, Video, Activity, Send, ShieldQuestion, Users,
} from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/context/AuthContext.jsx';
import * as R from '@/config/roles.js';
import { fmtRoleLabel, initialsOf } from '@/utils/format.js';

/*
|------------------------------------------------------------------------------
| Per-role dashboard shell with themed sidebar.
| The `variant` prop is set per route in App.jsx — the sidebar nav, accent
| colour, and primary-channel header all derive from it.
|------------------------------------------------------------------------------
*/

// Role lists live in @/config/roles.js, one constant per API gate. Declaring
// them here as well is what let the two drift: an item shown to a role the
// endpoint rejects renders a page whose only content is a permissions error,
// and an operator cannot tell that apart from an outage.

const VARIANTS = {
  citizen: {
    label:        'Citizen',
    accent:       'sky',
    sidebarBg:    'bg-slate-900',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-sky-600',
    nav: [
      { to: '/citizen',                label: 'Home',                 icon: Home },
      { to: '/citizen/submit',         label: 'Submit a report',      icon: FileText },
      { to: '/citizen/reports',        label: 'My reports',           icon: ScrollText },
      { to: '/citizen/challenges',     label: 'Identity challenges',  icon: BellIcon },
      { to: '/citizen/bolos',          label: 'Active alerts',        icon: BoloIcon },
      { to: '/citizen/cameras',        label: 'My cameras',           icon: CameraIcon },
      { to: '/citizen/vehicles',       label: 'My vehicles',          icon: Car },
      { to: '/citizen/ifps-enrollment',label: 'IFPS notifications',   icon: ShieldCheck },
    ],
  },
  agency: {
    label:        'Agency',
    accent:       'rose',
    sidebarBg:    'bg-slate-900',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-rose-600',
    // Most of this shell is agency-scoped work: the API requires an agency
    // membership and refuses without one, so hub_admin and inspector_general
    // cannot use it however senior they are. They have /hub for oversight.
    nav: [
      { to: '/agency',           label: 'Dashboard',        icon: LayoutDashboard, roles: R.RERR_AGENCY },
      { to: '/agency/dispatch',  label: 'Dispatch board',   icon: Map,             roles: R.RERR_AGENCY },
      { to: '/agency/manifests', label: 'Manifests',        icon: Inbox,           roles: R.RERR_AGENCY },
      // The live map reads both CAD and the agency's own queue, so it needs
      // whichever list is narrower.
      { to: '/agency/cad',       label: 'CAD live map',     icon: Target,          roles: R.RERR_AGENCY },
      { to: '/agency/units',     label: 'Field units',      icon: Radio,           roles: R.RERR_AGENCY },
      { to: '/agency/bolo',      label: 'BOLO',             icon: BoloIcon,        roles: R.RERR_AGENCY },
      { to: '/agency/sensors',   label: 'Sensor sources',   icon: Cctv,            roles: R.VISION_AGENCY },
      { to: '/agency/fleet',     label: 'Fleet telematics', icon: Truck,           roles: R.VISION_AGENCY_SCOPED },
      { to: '/agency/audit',     label: 'Audit viewer',     icon: ListTree,        roles: R.RERR_AGENCY },
      { to: '/agency/judicial',  label: 'Judicial',         icon: Gavel,           roles: R.JUDICIAL_OFFICER },
    ],
  },
  ifps: {
    label:        'IFPS',
    accent:       'sky',
    sidebarBg:    'bg-slate-900',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-sky-600',
    // Two audiences share this sidebar and they see almost nothing in common.
    //
    // Everything down to Partners is a government-operator surface backed by
    // /ifps/admin/*, which the API gates on ifps_supervisor|hub_admin. An
    // institutional partner was shown all of it and got "User does not have
    // the right roles" on every one — including the Dashboard, which loads
    // escalations and disputes from the same admin block. Untagged items
    // default to visible, so the gate has to be explicit here.
    nav: [
      { to: '/ifps',              label: 'Dashboard',         icon: LayoutDashboard,  roles: R.IFPS_OPERATOR },
      { to: '/ifps/challenges',   label: 'Challenge centre',  icon: BellIcon,         roles: R.IFPS_OPERATOR },
      { to: '/ifps/identities',   label: 'Identity states',   icon: UserCheck,        roles: R.IFPS_OPERATOR },
      { to: '/ifps/disputes',     label: 'Disputes',          icon: MessagesSquare,   roles: R.IFPS_OPERATOR },
      { to: '/ifps/escalations',  label: 'Escalations',       icon: Scale,            roles: R.IFPS_OPERATOR },
      { to: '/ifps/queue',        label: 'Adjudication queue',icon: ClipboardList,    roles: R.IFPS_OPERATOR },
      { to: '/ifps/transactions', label: 'Transactions',      icon: Activity,         roles: R.IFPS_OPERATOR },
      { to: '/ifps/partners',     label: 'Partners',          icon: Building2,        roles: R.IFPS_OPERATOR },
      // Partner-facing console. Hidden from government operators, who have
      // the cross-institution transaction monitor above instead.
      { to: '/ifps/signals',      label: 'My signals',        icon: Inbox,            roles: R.IFPS_PARTNER },
      { to: '/ifps/our-challenges', label: 'Our challenges',  icon: ShieldQuestion,   roles: R.IFPS_PARTNER },
    ],
  },
  firl: {
    label:        'FIRL',
    accent:       'emerald',
    sidebarBg:    'bg-slate-900',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-emerald-600',
    nav: [
      // The dashboard draws on casework and monitoring at once, so it needs
      // both gates — a narrower audience than either page beneath it.
      { to: '/firl',              label: 'Dashboard',     icon: LayoutDashboard, roles: R.FIRL_ENFORCEMENT },
      { to: '/firl/ffos',         label: 'FFOs',          icon: Target,          roles: R.FIRL_CASEWORK },
      { to: '/firl/gates',        label: 'Gate monitor',  icon: GitBranch,       roles: R.FIRL_CASEWORK },
      { to: '/firl/anomalies',    label: 'Anomalies',     icon: SearchIcon,      roles: R.FIRL_MONITORING },
      { to: '/firl/enforcement',  label: 'Enforcement',   icon: AlertCircle,     roles: R.FIRL_ENFORCEMENT },
    ],
  },
  veris: {
    label:        'VERIS',
    accent:       'amber',
    sidebarBg:    'bg-slate-900',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-amber-600',
    nav: [
      { to: '/veris',             label: 'Dashboard',      icon: LayoutDashboard, roles: R.VERIS_CASEWORK },
      { to: '/veris/queue',       label: 'Registrations',  icon: Workflow,        roles: R.VERIS_CASEWORK },
      { to: '/veris/dedup',       label: 'Dedup matches',  icon: Copy,            roles: R.VERIS_CASEWORK },
      { to: '/veris/eligibility', label: 'Eligibility',    icon: Vote,            roles: R.VERIS_CASEWORK },
      // Deliberately narrower than the rest: the API restricts dual control to
      // adjudication roles and propagation to VERIS administrators.
      { to: '/veris/dual-control',label: 'Dual control',   icon: ShieldCheck,     roles: R.VERIS_DUAL_CONTROL },
      { to: '/veris/propagation', label: 'Propagation',    icon: Send,            roles: R.VERIS_PROPAGATION },
      { to: '/veris/disputes',    label: 'Disputes',       icon: MessagesSquare,  roles: R.VERIS_CASEWORK },
    ],
  },
  hub: {
    label:        'Hub',
    accent:       'sky',
    sidebarBg:    'bg-slate-950',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-sky-600',
    nav: [
      // The hub shell is shared by several oversight roles, and they can use
      // very different parts of it. The first five come from hub.php and
      // rerr.php's hub block, both of which accept hub_admin alone — an
      // inspector general or a judge reaching them gets a permissions error,
      // so they are gated to match rather than shown to everyone.
      { to: '/hub',         label: 'Dashboard',      icon: LayoutDashboard, roles: R.HUB_OVERSIGHT },
      { to: '/hub/reports', label: 'Report intake',  icon: ScrollText,      roles: R.HUB_OVERSIGHT },
      { to: '/hub/audit',   label: 'Master audit',   icon: ListTree,        roles: R.HUB_OVERSIGHT },
      { to: '/hub/routing', label: 'Routing log',    icon: GitBranch,       roles: R.HUB_OVERSIGHT },
      { to: '/hub/agencies',label: 'Agency registry',icon: Building2,       roles: R.HUB_OVERSIGHT },
      // These do admit oversight roles beyond hub_admin.
      { to: '/hub/vision',  label: 'Threat bundles', icon: Cctv,            roles: R.VISION_ADMIN },
      { to: '/hub/vision/streams', label: 'Live streams', icon: Video,      roles: R.VISION_ADMIN },
      { to: '/hub/cbim',    label: 'CBIM ops',       icon: BoloIcon,        roles: R.VISION_ADMIN },
      { to: '/hub/nsa',     label: 'NS alerts',      icon: ShieldAlert,     roles: R.NSA_READ },
      { to: '/hub/pmics',   label: 'PMICS',          icon: HeartPulse,      roles: R.REGISTRY },
      { to: '/hub/osint',   label: 'OSINT',          icon: Globe,           roles: R.OSINT_READ },
      { to: '/hub/csdr',    label: 'CSDR registry',  icon: Network,         roles: R.REGISTRY },
      { to: '/hub/cdms',    label: 'CDMS registry',  icon: KeyRound,        roles: R.REGISTRY },
      { to: '/judicial',    label: 'Judicial inbox', icon: Gavel,           roles: R.JUDICIAL_REVIEWER },
      // Must match the route gates in App.jsx. Settings is platform_admin
      // only — that is the sole role /api/platform/* accepts, so showing the
      // link to anyone else advertises a page whose every call would 403.
      { to: '/hub/roadmap',  label: 'Roadmap',  icon: Compass,  roles: ['hub_admin','platform_admin','super_admin'] },
      { to: '/hub/internal-users', label: 'Internal access', icon: Users, roles: R.PLATFORM_ADMIN },
      { to: '/hub/settings', label: 'Settings', icon: Settings, roles: R.PLATFORM_ADMIN },
    ],
  },
  judicial: {
    label:        'Judicial',
    accent:       'amber',
    sidebarBg:    'bg-slate-950',
    sidebarText:  'text-slate-100',
    activeBg:     'bg-amber-600',
    nav: [
      { to: '/judicial', label: 'Inbox', icon: Gavel, roles: R.JUDICIAL_REVIEWER },
    ],
  },
};

/**
 * Pick a sidebar variant from a user's roles.
 *
 * Account settings is reachable by every role, so its route cannot hard-code
 * a variant the way subsystem routes do — the shell has to follow whoever is
 * signed in. Order matters: the first match wins, so the more specific
 * operator roles are listed ahead of the broad hub ones.
 */
const VARIANT_BY_ROLE = {
  citizen:               'citizen',
  agency_officer:        'agency',
  agency_supervisor:     'agency',
  agency_admin:          'agency',
  ifps_supervisor:       'ifps',
  institutional_partner: 'ifps',
  firl_admin:            'firl',
  program_officer:       'firl',
  veris_admin:           'veris',
  election_official:     'veris',
  judge:                 'judicial',
  magistrate:            'judicial',
  prosecutor:            'judicial',
  hub_admin:             'hub',
  inspector_general:     'hub',
  platform_admin:        'hub',
  super_admin:           'hub',
};

export function variantForRoles(roles = []) {
  for (const r of roles) {
    if (VARIANT_BY_ROLE[r]) return VARIANT_BY_ROLE[r];
  }
  return 'citizen';
}

/** Layout that follows the signed-in user's role rather than the route. */
export function RoleLayout() {
  const { roles } = useAuth();
  return <Layout variant={variantForRoles(roles)} />;
}

export default function Layout({ variant = 'citizen' }) {
  const cfg = VARIANTS[variant] ?? VARIANTS.citizen;
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar — desktop */}
      <aside className={clsx('hidden lg:flex lg:w-64 lg:flex-col', cfg.sidebarBg, cfg.sidebarText)}>
        <SidebarBody cfg={cfg} variant={variant} />
      </aside>

      {/* Sidebar — mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/60" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className={clsx('absolute left-0 top-0 flex h-full w-72 flex-col', cfg.sidebarBg, cfg.sidebarText)}>
            <SidebarBody cfg={cfg} variant={variant} onNavigate={() => setMobileOpen(false)} />
          </aside>
        </div>
      )}

      {/* Right column */}
      <div className="flex flex-1 flex-col">
        <TopBar
          cfg={cfg}
          onOpenMobile={() => setMobileOpen(true)}
          pathname={pathname}
        />
        <main className="flex-1 px-4 py-6 md:px-8 md:py-10">
          <div className="mx-auto w-full max-w-7xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

function SidebarBody({ cfg, variant, onNavigate }) {
  const { hasRole } = useAuth();
  const visibleNav = cfg.nav.filter(item => !item.roles || hasRole(...item.roles));

  return (
    <>
      <div className="flex h-16 items-center gap-2 border-b border-white/10 px-5">
        <Link to="/" className="flex items-center gap-2" onClick={onNavigate}>
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-white/10">
            <Shield className="h-5 w-5" strokeWidth={2.4} />
          </div>
          <div className="leading-tight">
            <div className="text-sm font-bold tracking-wide">NACIS</div>
            <div className="text-[10px] uppercase tracking-[0.14em] opacity-70">{cfg.label}</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {visibleNav.map(item => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/' || item.to.endsWith(variant === 'hub' ? '/hub' : `/${variant}`)}
              onClick={onNavigate}
              className={({ isActive }) => clsx(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition',
                isActive ? clsx(cfg.activeBg, 'text-white shadow-sm') : 'text-slate-300 hover:bg-white/5 hover:text-white',
              )}
            >
              <Icon className="h-4 w-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          );
        })}
      </nav>

      <div className="border-t border-white/10 p-4">
        <UserChip />
      </div>
    </>
  );
}

function UserChip() {
  const { user, logout, roles } = useAuth();
  const [open, setOpen] = useState(false);

  if (!user) return null;
  const primaryRole = roles?.[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition hover:bg-white/5"
      >
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs font-bold">
          {initialsOf(user.name || user.email)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{user.name || user.email}</div>
          <div className="truncate text-[11px] opacity-70">{fmtRoleLabel(primaryRole) || 'Authenticated'}</div>
        </div>
        <ChevronDown className={clsx('h-4 w-4 opacity-70 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute bottom-full left-0 right-0 mb-2 overflow-hidden rounded-md border border-white/10 bg-slate-900 shadow-xl">
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-sm text-slate-200 hover:bg-white/10"
          >
            Account settings
          </Link>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="block border-t border-white/10 px-3 py-2 text-sm text-slate-200 hover:bg-white/10"
          >
            Public site
          </Link>
          <button
            type="button"
            onClick={() => { setOpen(false); logout(); }}
            className="flex w-full items-center gap-2 border-t border-white/10 px-3 py-2 text-sm text-rose-200 hover:bg-white/10"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/*
 * The top bar labels the current area from the URL. Detail routes end in a
 * record identifier, and rendering that segment produced titles like
 * "A26f37c7 Fc86 4ebd Afd0 F0fefcbe34b8" — the id with its hyphens turned
 * into spaces and title-cased by the `capitalize` class. Identifiers are
 * skipped so the bar falls back to the nearest meaningful segment; the page's
 * own PageHeader already shows the record it belongs to.
 */
const looksLikeIdentifier = seg =>
  /^[0-9a-f-]{16,}$/i.test(seg)          // uuid
  || /^\d+$/.test(seg)                    // numeric id
  || /^[A-Z]{2,5}-\d{4}-[A-Z0-9]{6,}$/i.test(seg)  // ISS-2026-XXXXXX style codes
  || /^(hub|man)-[A-Za-z0-9]{8,}$/i.test(seg);     // trace / manifest refs

function TopBar({ cfg, onOpenMobile, pathname }) {
  const { t } = useTranslation();
  const segments = pathname.split('/').filter(Boolean);
  const label = [...segments].reverse().find(s => !looksLikeIdentifier(s));
  const title = label ? label.replace(/-/g, ' ') : cfg.label;

  return (
    <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
      <div className="flex h-16 items-center gap-3 px-4 md:px-8">
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-700 hover:bg-slate-100 lg:hidden"
          onClick={onOpenMobile}
          aria-label={t('common.openMenu')}
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{cfg.label}</div>
          <h1 className="text-base font-semibold capitalize text-slate-900">{title}</h1>
        </div>
        <button type="button" className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100" aria-label="Search">
          <Search className="h-4 w-4" />
        </button>
        <button type="button" className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100" aria-label="Notifications">
          <Bell className="h-4 w-4" />
        </button>
        <LanguageSwitcher variant="light" />
        <TopBarUserMenu />
      </div>
    </header>
  );
}

function TopBarUserMenu() {
  const { t } = useTranslation();
  const { user, logout, roles } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onMouseDown = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (!user) return null;
  const primaryRole = roles?.[0];

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 rounded-md px-1.5 py-1.5 transition hover:bg-slate-100"
        aria-expanded={open}
        aria-label={t('common.signOut')}
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-900 text-xs font-bold text-white">
          {initialsOf(user.name || user.email)}
        </span>
        <ChevronDown className={clsx('h-3.5 w-3.5 text-slate-500 transition', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-2 w-60 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 px-4 py-3">
            <div className="truncate text-sm font-semibold text-slate-900">{user.name || user.email}</div>
            <div className="truncate text-xs text-slate-500">{user.email || user.phone}</div>
            {primaryRole && (
              <div className="mt-1 inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600">
                {fmtRoleLabel(primaryRole)}
              </div>
            )}
          </div>
          <Link
            to="/account"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t('topbar.account', 'Account settings')}
          </Link>
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="block px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            {t('topbar.publicSite', 'Public site')}
          </Link>
          <button
            type="button"
            onClick={() => { setOpen(false); logout(); }}
            className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2 text-sm text-rose-600 hover:bg-rose-50"
          >
            <LogOut className="h-4 w-4" /> {t('common.signOut')}
          </button>
        </div>
      )}
    </div>
  );
}
